import { useEffect, useState } from "react";
import {
  BASKET_ADD_ITEM,
  BASKET_COUNT_ITEMS,
  BASKET_DELETE,
  BASKET_DELETE_ITEM,
  BASKET_GET_ITEMS,
  BASKET_GET_ITEM_IDS,
} from "./endpoints";
import type {
  IAxiosInstance,
  TAddToCart,
  TCartDetailedItem,
  TCartDetailedItems,
  TCartItem,
  TCartItems,
  TDeleteCartItem,
} from "./types";

export const UPDATE_CART_EVENT = "UPDATE_CART_EVENT";

export const dispatchCartUpdate = () => {
  if (typeof window !== "undefined")
    window.dispatchEvent(new Event(UPDATE_CART_EVENT));
};

export function isDetailedCartItem(
  item: TCartItem | TCartDetailedItem,
): item is TCartDetailedItem {
  return "timestamp" in item;
}

export function isDetailedCartItems(
  item: TCartItems | TCartDetailedItems,
): item is TCartDetailedItems {
  return isDetailedCartItem(item[0]);
}

export class Cart {
  private _items: TCartItems = [];
  private _detailedItems: TCartDetailedItems | undefined;
  api: IAxiosInstance;
  fetchingCart?: Promise<unknown>;
  fetchingDetailedCart?: Promise<unknown>;

  constructor(api: IAxiosInstance) {
    this.api = api;
    if (typeof window !== "undefined") {
      globalThis.cart = this;
      this.getCart();
    }
  }
  getCart() {
    const promises = [
      this.api
        .get<TCartItems>(BASKET_GET_ITEM_IDS(), { withCredentials: true })
        .then((res) => {
          this.items = res.data;
        }),
      this.hasDetailItems && this.getDetailedCart(),
    ].filter(Boolean);
    this.fetchingCart = Promise.all(promises);
    return this.fetchingCart;
  }
  getDetailedCart() {
    this.fetchingDetailedCart = this.api
      .get<TCartDetailedItems>(BASKET_GET_ITEMS(), { withCredentials: true })
      .then((res) => {
        this._detailedItems = res.data;
        dispatchCartUpdate();
      });
    return this.fetchingDetailedCart;
  }
  get hasDetailItems() {
    return !!this._detailedItems;
  }
  get items() {
    return this._detailedItems || this._items;
  }
  set items(val: TCartItems | TCartDetailedItems) {
    if (val.length) {
      if (isDetailedCartItems(val)) {
        this._items = val;
        this._detailedItems = val;
      } else {
        this._items = val;
      }
    } else {
      this._items = val;
    }
    dispatchCartUpdate();
  }
  useCartItems() {
    const [items, setItems] = useState(this._items);
    useEffect(() => {
      const handler = () => {
        setItems(JSON.parse(JSON.stringify(this._items || [])));
      };
      window.addEventListener(UPDATE_CART_EVENT, handler);
      return () => {
        window.removeEventListener(UPDATE_CART_EVENT, handler);
      };
    }, []);
    return items;
  }
  useCartDetailedItems() {
    const [items, setItems] = useState(this._detailedItems);
    useEffect(() => {
      const handler = () => {
        setItems(JSON.parse(JSON.stringify(this._detailedItems || [])));
      };
      if (!this.hasDetailItems && !this.fetchingDetailedCart) {
        this.getDetailedCart().then(dispatchCartUpdate);
      }
      window.addEventListener(UPDATE_CART_EVENT, handler);
      return () => {
        window.removeEventListener(UPDATE_CART_EVENT, handler);
      };
    }, []);
    return items;
  }
  add(item: TAddToCart) {
    return this.api
      .put(BASKET_ADD_ITEM(), item, { withCredentials: true })
      .then(this.getCart.bind(this));
  }
  remove(item: TDeleteCartItem) {
    if (
      !this._items.filter(
        (cartItem) =>
          cartItem.productId === item.productId ||
          item.productPackageId === cartItem.productPackageId,
      ).length
    )
      return Promise.reject("item not found");

    return this.api
      .put(BASKET_DELETE_ITEM(), item, { withCredentials: true })
      .then(this.getCart.bind(this));
  }

  async interactiveRemove(item: TDeleteCartItem) {
    if (
      !this._items.filter(
        (cartItem) =>
          cartItem.productId === item.productId ||
          item.productPackageId === cartItem.productPackageId,
      ).length
    )
      return Promise.reject("item not found");

    return this.api
      .put(BASKET_DELETE_ITEM(), item, { withCredentials: true })
      .then((response) => {
        this.getCart.bind(this);
        return response;
      });
  }

  get(id: number, targetGroupId: number) {
    return this._items.find(
      (item) =>
        item.targetGroupId === targetGroupId &&
        (item.productId === id || item.productPackageId === id),
    );
  }
  has(id: number, targetGroupId: number) {
    return !!this.get(id, targetGroupId);
  }
  deleteBasket() {
    return this.api
      .delete(BASKET_DELETE(), { withCredentials: true })
      .then(this.getCart.bind(this));
  }
  getCount() {
    return this.api.$get<number>(BASKET_COUNT_ITEMS(), {
      withCredentials: true,
    });
  }
}

export default function createCart(api: IAxiosInstance) {
  return new Cart(api);
}
